/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Complexe = require('../types/Complexe')
const Opef2v = require('../types/Opef2v')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CCbGlob = require('../kernel/CCbGlob')

module.exports = CFonction2Var

/**
 * Classe représentant dans un arbre binaire de calcul un appel de fonction
 * prédéfinie à deux variables : maxi, mini, gcd, lcm, ncr, npr ou mod.
 * @constructor
 * @extends CCb
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {Integer} Opef : Entier donnant l'opérateur (voir OperateurFonction mtg32.Opef)
 * @param {CCb} operande1 : Le premier opérande.
 * @param {CCb} operande2 : Le deuxième opérande.
 * @returns {CFonction2Var}
 */
function CFonction2Var (listeProprietaire, Opef, operande1, operande2){
  CCb.call(this, listeProprietaire);
  if (arguments.length !== 1) {
    this.Opef = Opef;
    this.operande1 = operande1;
    this.operande2 = operande2;
  }
  this.z1loc = new Complexe();
  this.z2loc = new Complexe();
}
CFonction2Var.prototype = new CCb();
CFonction2Var.prototype.constructor = CFonction2Var;
CFonction2Var.prototype.superClass = "CCb";
CFonction2Var.prototype.className = "CFonction2Var";


CFonction2Var.prototype.nombreVariables = function() {
  return 2;
};

CFonction2Var.prototype.nature = function() {
  return CCbGlob.natFonction2Var;
};

CFonction2Var.prototype.getClone = function(listeSource, listeCible) {
  var cloneOperande1 = this.operande1.getClone(listeSource, listeCible);
  var cloneOperande2 = this.operande2.getClone(listeSource, listeCible);
  return new CFonction2Var(listeCible, this.Opef, cloneOperande1, cloneOperande2);
};

CFonction2Var.prototype.initialisePourDependance = function() {
  CCb.prototype.initialisePourDependance.call(this);
  this.operande1.initialisePourDependance();
  this.operande2.initialisePourDependance();
};

CFonction2Var.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CCb.prototype.dependDe.call(this,p) ||
    this.operande1.dependDe(p) || this.operande2.dependDe(p));
};

CFonction2Var.prototype.dependDePourBoucle = function(p) {
  return this.operande1.dependDePourBoucle(p) || this.operande2.dependDePourBoucle(p);
};

CFonction2Var.prototype.existe = function(infoRandom) {
  return this.operande1.existe(infoRandom) && this.operande2.existe(infoRandom);
};
/**
 * Renvoie le résultat de la fonction réelle prédéfinie appliquée
 * aux opérandes x et y.
 * @param {Boolean} infoRandom : true si les calculs aléatoires par rand
 * soient réactualisés.
 * @param {Float} x : Le premier opérande
 * @param {Float} y : Le second opérande
 * @returns {Float}
 */
CFonction2Var.prototype.resultatBase = function(infoRandom, x, y) {
  switch (this.Opef)
  {
    case Opef2v.maxi:
      if (x > y) return x; else return y;
    case Opef2v.mini:
      if (x < y) return x; else return y;
    case Opef2v.gcd:
      if ((x !== Math.floor(x)) || (y !== Math.floor(y)) || ((x === 0) && (y === 0))
          || (x < 0) || (y < 0))
        throw new Error(kernel.erreurCalculException);
      else return kernel.pgcd(x, y);
    case Opef2v.lcm:
      if ((x !== Math.floor(x)) || (y !== Math.floor(y)) || (x < 0) || (y < 0))
        throw new Error(kernel.erreurCalculException);
      else return kernel.ppcm(x, y);
    case Opef2v.ncr:
      if ((x !== Math.floor(x)) || (y !== Math.floor(y)) || (x < 0) || (y < 0) || (y > x))
        throw new Error(kernel.erreurCalculException);
      else return kernel.ncr(x, y);
    case Opef2v.npr:
      if ((x !== Math.floor(x)) || (y !== Math.floor(y)) || (x < 0) || (y < 0) || (y > x))
        throw new Error(kernel.erreurCalculException);
      else return kernel.npr(x, y);
    case Opef2v.mod:
      if ((x !== Math.floor(x)) || (y !== Math.floor(y)) || (x < 0) || (y <= 0))
        throw new Error(kernel.erreurCalculException);
      else return Math.floor(x - Math.floor(x/y)*y + 0.5);

    default : throw new Error(kernel.erreurCalculException);
  }
};

CFonction2Var.prototype.resultat = function(infoRandom){
  var x1 = this.operande1.resultat(infoRandom);
  var y1 = this.operande2.resultat(infoRandom);
  return this.resultatBase(infoRandom, x1, y1);
};

CFonction2Var.prototype.resultatFonction = function(infoRandom, valeurParametre) {
  var x1 = this.operande1.resultatFonction(infoRandom, valeurParametre);
  var y1 = this.operande2.resultatFonction(infoRandom, valeurParametre);
  return this.resultatBase(infoRandom, x1, y1);
};
/**
 * Renvoie le résultat de la fonction réelle prédéfinie appliquée
 * aux opérandes complexes z1 et z2.
 * @param {Boolean} infoRandom : true si les calculs aléatoires par rand doivent
 * être réactualisés.
 * @param {Complexe} z1 : Le premier opérande.
 * @param {Complexe} z2 : Le second opérande.
 * @param {Complexe} zRes : Contient au retour le résultat.
 * @returns {undefined}
 */
CFonction2Var.prototype.resultatComplexeBase = function(infoRandom, z1, z2, zRes) {
  zRes.y = 0;
  if ((z1.y !== 0) || (z2.y !== 0)) throw new Error(kernel.erreurCalculException);
  switch (this.Opef) {
    case Opef2v.maxiC:
      if (z1.x > z2.x) zRes.x = z1.x; else zRes.x = z2.x;
      break;
    case Opef2v.miniC:
      if (z1.x < z2.x) zRes.x = z1.x; else zRes.x = z2.x;
      break;
    case Opef2v.gcdC:
      if ((z1.x !== Math.floor(z1.x)) || (z2.x !== Math.floor(z2.x)) || ((z1.x === 0) && (z2.x === 0))
          || (z1.x < 0) || (z1.x < 0)) throw new Error(kernel.erreurCalculException);
      else zRes.x = kernel.pgcd(z1.x, z2.x);
      break;
    case Opef2v.lcmC:
      if ((z1.x !== Math.floor(z1.x)) || (z2.x !== Math.floor(z2.x)) || (z1.x < 0) || (z2.x < 0))
        throw new Error(kernel.erreurCalculException);
      else zRes.x = kernel.ppcm(z1.x, z2.x);
      break;
    case Opef2v.ncrC:
      if ((z1.x !== Math.floor(z1.x)) || (z2.x !== Math.floor(z2.x)) || (z1.x < 0) || (z2.x < 0) || (z2.x > z1.x))
        throw new Error(kernel.erreurCalculException);
      else zRes.x = kernel.ncr(z1.x, z2.x);
      break;
    case Opef2v.nprC:
      if ((z1.x !== Math.floor(z1.x)) || (z2.x !== Math.floor(z2.x)) || (z1.x < 0) || (z2.x < 0) || (z2.x > z1.x))
        throw new Error(kernel.erreurCalculException);
      else zRes.x = kernel.npr(z1.x, z2.x);
      break;
    case Opef2v.modC:
      if ((z1.x !== Math.floor(z1.x)) || (z2.x !== Math.floor(z2.x)) || (z1.x < 0) || (z2.x <= 0))
        throw new Error(kernel.erreurCalculException);
      else zRes.x =  Math.floor(z1.x - Math.floor(z1.x/z2.x)*z2.x + 0.5);
      break;
    default : throw new Error(kernel.erreurCalculException);
  }
};

CFonction2Var.prototype.resultatComplexe = function(infoRandom, zRes) {
  this.operande1.resultatComplexe(infoRandom, this.z1loc);
  this.operande2.resultatComplexe(infoRandom, this.z2loc);
  this.resultatComplexeBase(infoRandom, this.z1loc, this.z2loc, zRes);
};

CFonction2Var.prototype.resultatFonctionComplexe = function(infoRandom, valeurParametre, zRes) {
  this.operande1.resultatFonctionComplexe(infoRandom, valeurParametre, this.z1loc);
  this.operande2.resultatFonctionComplexe(infoRandom, valeurParametre, this.z2loc);
  this.resultatComplexeBase(infoRandom, this.z1loc, this.z2loc, zRes);
};

CFonction2Var.prototype.dependDeVariable = function(ind) {
  return this.operande1.dependDeVariable(ind) || this.operande2.dependDeVariable(ind);
};

CFonction2Var.prototype.getCopie = function() {
  return new CFonction2Var(this.listeProprietaire, this.Opef, this.operande1.getCopie(), this.operande2.getCopie());
};

CFonction2Var.prototype.chaineCalcul = function(varFor) {
  var parouv = "(";
  var parfer = ")";
  var cha1 = this.operande1.chaineCalculSansPar(varFor);
  var cha2 = this.operande2.chaineCalculSansPar(varFor);
  var ch = parouv + cha1 + "," + cha2 + parfer;

  switch (this.Opef)
  {
    case Opef2v.maxi :
    case Opef2v.maxiC :
      return kernel.getString("maxi") + ch;
    case Opef2v.mini :
    case Opef2v.miniC :
      return kernel.getString("mini") + ch;
    case Opef2v.gcd :
    case Opef2v.gcdC :
      return kernel.getString("gcd") + ch;
    case Opef2v.lcm :
    case Opef2v.lcmC :
      return kernel.getString("lcm") + ch;
    case Opef2v.ncr :
    case Opef2v.ncrC :
      return kernel.getString("ncr") + ch;
    case Opef2v.npr :
    case Opef2v.nprC :
      return kernel.getString("npr") + ch;
    case Opef2v.mod :
    case Opef2v.modC :
      return kernel.getString("mod") + ch;
    default : return "";
  }
};

CFonction2Var.prototype.chaineLatex = function(varFor) {
var parouvLatex = "\\left(";
var parferLatex = "\\right)";
  var cha1 = this.operande1.chaineLatexSansPar(varFor);
  var cha2 = this.operande2.chaineLatexSansPar(varFor);
  var ch = parouvLatex + cha1 + "," + cha2 + parferLatex;

  switch (this.Opef)
  {
    case Opef2v.maxi :
    case Opef2v.maxiC :
      return kernel.getString("maxi") + ch;
    case Opef2v.mini :
    case Opef2v.miniC :
      return kernel.getString("mini") + ch;
    case Opef2v.gcd :
    case Opef2v.gcdC :
      return kernel.getString("gcd") + ch;
    case Opef2v.lcm :
    case Opef2v.lcmC :
      return kernel.getString("lcm") + ch;
    case Opef2v.ncr :
    case Opef2v.ncrC :
      return kernel.getString("ncr") + ch;
    case Opef2v.npr :
    case Opef2v.nprC :
      return kernel.getString("npr") + ch;
    case Opef2v.mod :
    case Opef2v.modC :
      return kernel.getString("mod") + ch;
    default : return "";
  }
};

CFonction2Var.prototype.read = function(inps, list) {
  CCb.prototype.read.call(this,inps, list);
  this.Opef = inps.readByte();
  this.operande1 = inps.readObject(list);
  this.operande2 = inps.readObject(list);
};

CFonction2Var.prototype.write = function(oups, list) {
  CCb.prototype.write.call(this, oups, list);
  oups.writeByte(this.Opef);
  oups.writeObject(this.operande1);
  oups.writeObject(this.operande2);
};

CFonction2Var.prototype.estConstant = function() {
  return (this.operande1.estConstant() && this.operande2.estConstant());
};

CFonction2Var.prototype.deriveePossible = function(indiceVariable) {
  return this.operande1.deriveePossible(indiceVariable) && this.operande2.deriveePossible(indiceVariable);
};

CFonction2Var.prototype.calculAvecValeursRemplacees = function() {
  return new CFonction2Var(this.listeProprietaire, this.Opef,
      this.operande1.calculAvecValeursRemplacees(), this.operande2.calculAvecValeursRemplacees());
};



// WEBPACK FOOTER //
// ./src/objets/CFonction2Var.js